import { useCallback, useEffect, useMemo, useState } from "react";

import { findPublicoTurma, findResumoVagasTurma, saveConfigVagasTurma } from "API/apiAcoes";
import { Cidade } from "types/Cidade";
import { useLocalidadeStore } from "store/useLocalidadeStore";

export type CriterioOcupacao = "LIVRE" | "RESPEITA_LIMITES" | "MISTO";
export type MetodoSelecaoMunicipio = "REGIONAL" | "MANUAL" | "CSV" | "ESTADO";
export type ModoDistribuicaoVagas = "IGUAL" | "SEM_LIMITE";

export type MunicipioSelecionado = Cidade & {
  limite_municipal: number;
  limite_estadual: number;
  origem?: string;
};

const sortMunicipiosByNome = (items: MunicipioSelecionado[]) =>
  [...items].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

const mapMunicipioResumo = (item: {
  id: number;
  codigo_ibge?: number;
  nome: string;
  sigla: string;
  estado_id: number;
  estado?: string;
  limite_municipal: number | null;
  limite_estadual: number | null;
  origem?: string;
}): MunicipioSelecionado => ({
  id: item.id,
  codigo_ibge: item.codigo_ibge ?? 0,
  nome: item.nome,
  sigla: item.sigla,
  estado_id: item.estado_id,
  estado: item.estado ?? "MG",
  limite_municipal: Number(item.limite_municipal) || 0,
  limite_estadual: Number(item.limite_estadual) || 0,
  origem: item.origem ?? "MANUAL",
});

export const useGestaoVagasTurma = (turmaId: string | null) => {
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [loadingConfigVagas, setLoadingConfigVagas] = useState(false);
  const [loadingRegionais, setLoadingRegionais] = useState(false);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const [metodoSelecao, setMetodoSelecao] = useState<MetodoSelecaoMunicipio>("REGIONAL");
  const [selectedRegional, setSelectedRegional] = useState<number[]>([]);
  const [selectedMunicipiosManual, setSelectedMunicipiosManual] = useState<number[]>([]);
  const [selectedMunicipioItems, setSelectedMunicipioItems] = useState<MunicipioSelecionado[]>([]);

  const [criterio, setCriterio] = useState<CriterioOcupacao>("LIVRE");
  const [semLimiteVagas, setSemLimiteVagas] = useState(false);
  const [limiteTotal, setLimiteTotal] = useState(0);
  const [limiteMunicipal, setLimiteMunicipal] = useState(0);
  const [limiteEstadual, setLimiteEstadual] = useState(0);

  const [modoDistribuicao, setModoDistribuicao] = useState<ModoDistribuicaoVagas>("SEM_LIMITE");
  const [limitePorMunicipio, setLimitePorMunicipio] = useState<number>(0);
  const [limitePorEstado, setLimitePorEstado] = useState<number>(0);
  const [permitirExcecoes, setPermitirExcecoes] = useState(false);

  const [permiteLimiteMunicipal, setPermiteLimiteMunicipal] = useState(false);
  const [permiteLimiteEstadual, setPermiteLimiteEstadual] = useState(false);
  const [publicoTurmaCarregado, setPublicoTurmaCarregado] = useState(false);

  const estados = useLocalidadeStore((state) => state.estados);
  const regionais = useLocalidadeStore((state) => state.regionais);
  const ensureEstados = useLocalidadeStore((state) => state.ensureEstados);
  const ensureRegionais = useLocalidadeStore((state) => state.ensureRegionais);
  const ensureCidades = useLocalidadeStore((state) => state.ensureCidades);

  const [municipios, setMunicipios] = useState<Cidade[]>([]);

  const selectedMunicipios = useMemo(
    () => selectedMunicipioItems.map((item) => item.id),
    [selectedMunicipioItems],
  );

  const mediaVagasPorMunicipio = useMemo(() => {
    if (selectedMunicipioItems.length === 0 || limiteTotal <= 0) {
      return 0;
    }
    return Math.round(limiteTotal / selectedMunicipioItems.length);
  }, [limiteTotal, selectedMunicipioItems.length]);

  const somaAmbitos = useMemo(
    () =>
      (permiteLimiteMunicipal ? Number(limiteMunicipal) || 0 : 0) +
      (permiteLimiteEstadual ? Number(limiteEstadual) || 0 : 0),
    [limiteEstadual, limiteMunicipal, permiteLimiteEstadual, permiteLimiteMunicipal],
  );

  const excedeLimiteTotal = somaAmbitos > limiteTotal;

  const ensureBaseData = useCallback(async () => {
    setLoadingMunicipios(true);
    setLoadingRegionais(true);
    try {
      const loadedEstados = await ensureEstados();
      await ensureRegionais();
      const estadoMG = loadedEstados.find((item) => item.sigla === "MG");
      if (!estadoMG) {
        setMsgError("Nao foi possivel localizar o estado MG.");
        return;
      }

      const cidades = await ensureCidades({ estado_id: estadoMG.estado_id });
      setMunicipios([...cidades].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")));
    } finally {
      setLoadingMunicipios(false);
      setLoadingRegionais(false);
    }
  }, [ensureCidades, ensureEstados, ensureRegionais]);

  useEffect(() => {
    ensureBaseData();
  }, [ensureBaseData]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const addMunicipios = useCallback((items: MunicipioSelecionado[]) => {
    setSelectedMunicipioItems((prev) => {
      const merged = new Map<number, MunicipioSelecionado>();

      prev.forEach((item) => merged.set(item.id, item));
      items.forEach((item) => {
        if (!merged.has(item.id)) {
          merged.set(item.id, item);
        }
      });

      return sortMunicipiosByNome(Array.from(merged.values()));
    });
  }, []);

  const addMunicipiosByIds = useCallback(
    (ids: number[], origem: string) => {
      const selected = municipios
        .filter((item) => ids.includes(item.id))
        .map((item) => ({
          ...item,
          limite_municipal: 0,
          limite_estadual: 0,
          origem,
        }));

      addMunicipios(selected);
    },
    [addMunicipios, municipios],
  );

  const addMunicipiosManual = useCallback(() => {
    if (selectedMunicipiosManual.length === 0) {
      setMsgError("Selecione ao menos um municipio para adicionar.");
      return false;
    }

    addMunicipiosByIds(selectedMunicipiosManual, "MANUAL");
    setSelectedMunicipiosManual([]);
    return true;
  }, [addMunicipiosByIds, selectedMunicipiosManual]);

  const addMunicipiosByRegionais = useCallback(() => {
    if (selectedRegional.length === 0) {
      setMsgError("Selecione ao menos um polo pre-cadastrado.");
      return false;
    }

    const ids = new Set<number>();
    regionais
      .filter((item) => selectedRegional.includes(item.id))
      .forEach((regional) => {
        regional.municipios?.forEach((municipio) => ids.add(municipio.id));
      });

    addMunicipiosByIds(Array.from(ids), "REGIONAL");
    return true;
  }, [addMunicipiosByIds, regionais, selectedRegional]);

  const addTodosMunicipiosEstado = useCallback(() => {
    if (municipios.length === 0) {
      setMsgError("Nenhum municipio de MG foi carregado.");
      return false;
    }

    addMunicipios(
      municipios.map((item) => ({
        ...item,
        limite_municipal: 0,
        limite_estadual: 0,
        origem: "ESTADO",
      })),
    );

    return true;
  }, [addMunicipios, municipios]);

  const addMunicipiosFromCsvImport = useCallback(
    (items: Array<{ codigo_ibge: number; limite_municipal: number; limite_estadual: number }>) => {
      const municipioMap = new Map<number, Cidade>();
      municipios.forEach((item) => municipioMap.set(item.codigo_ibge, item));

      const imported = items
        .map((item) => {
          const municipio = municipioMap.get(item.codigo_ibge);
          if (!municipio) return null;

          return {
            ...municipio,
            limite_municipal: item.limite_municipal ?? 0,
            limite_estadual: item.limite_estadual ?? 0,
            origem: "CSV",
          } as MunicipioSelecionado;
        })
        .filter((item): item is MunicipioSelecionado => Boolean(item));

      addMunicipios(imported);
    },
    [addMunicipios, municipios],
  );

  const removeMunicipio = useCallback((municipioId: number) => {
    setSelectedMunicipioItems((prev) => prev.filter((item) => item.id !== municipioId));
  }, []);

  const clearMunicipios = useCallback(() => {
    setSelectedMunicipioItems([]);
  }, []);

  const setLimiteMunicipalSafe = useCallback(
    (value: number) => {
      const sanitized = Math.max(0, Number(value) || 0);
      const outro = permiteLimiteEstadual ? Number(limiteEstadual) || 0 : 0;
      const maximo = Math.max(limiteTotal - outro, 0);
      setLimiteMunicipal(Math.min(sanitized, maximo));
    },
    [limiteEstadual, limiteTotal, permiteLimiteEstadual],
  );

  const setLimiteEstadualSafe = useCallback(
    (value: number) => {
      const sanitized = Math.max(0, Number(value) || 0);
      const outro = permiteLimiteMunicipal ? Number(limiteMunicipal) || 0 : 0;
      const maximo = Math.max(limiteTotal - outro, 0);
      setLimiteEstadual(Math.min(sanitized, maximo));
    },
    [limiteMunicipal, limiteTotal, permiteLimiteMunicipal],
  );

  const togglePermitirExcecoes = useCallback(
    (checked: boolean) => {
      setPermitirExcecoes(checked);
      if (!checked) return;

      setSelectedMunicipioItems((prev) =>
        prev.map((item) => ({
          ...item,
          limite_municipal: permiteLimiteMunicipal ? Number(limitePorMunicipio) || 0 : 0,
          limite_estadual: permiteLimiteEstadual ? Number(limitePorEstado) || 0 : 0,
        })),
      );
    },
    [limitePorEstado, limitePorMunicipio, permiteLimiteEstadual, permiteLimiteMunicipal],
  );

  const updateMunicipioNumVagas = useCallback(
    (id: number, campo: "limite_municipal" | "limite_estadual", limite: number) => {
      const sanitized = Math.max(0, Number(limite) || 0);
      setSelectedMunicipioItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, [campo]: sanitized } : item)),
      );
    },
    [],
  );

  const validateForm = () => {
    if (selectedMunicipioItems.length === 0) {
      setMsgError("Adicione pelo menos um municipio participante.");
      return false;
    }
    if (!semLimiteVagas && limiteTotal <= 0) {
      setMsgError("Preencha a quantidade total de vagas da turma.");
      return false;
    }
    if (!criterio) {
      setMsgError("Selecione um criterio de ocupacao.");
      return false;
    }
    if (!semLimiteVagas && criterio === "MISTO" && excedeLimiteTotal) {
      setMsgError("A soma das vagas municipal e estadual nao pode ultrapassar o total.");
      return false;
    }
    return true;
  };

  const saveConfigVagas = async (): Promise<boolean> => {
    if (!turmaId) {
      setMsgError("Nao foi possivel encontrar o codigo da turma.");
      return false;
    }
    if (!validateForm()) {
      return false;
    }

    setLoadingSave(true);
    try {
      const distribuicaoModo =
        semLimiteVagas || modoDistribuicao === "SEM_LIMITE"
          ? "SEM_LIMITE"
          : permitirExcecoes
            ? "DIFERENTE"
            : "IGUAL";

      const payload = {
        sem_limite_vagas: semLimiteVagas,
        escopo: {
          tipo: "MANUAL",
          municipios: selectedMunicipios,
        },
        criterio_ocupacao: semLimiteVagas ? "LIVRE" : criterio,
        limites: {
          total: semLimiteVagas ? 0 : limiteTotal,
          ambitos: {
            municipal:
              !semLimiteVagas && criterio === "MISTO" && permiteLimiteMunicipal
                ? Number(limiteMunicipal) || 0
                : 0,
            estadual:
              !semLimiteVagas && criterio === "MISTO" && permiteLimiteEstadual
                ? Number(limiteEstadual) || 0
                : 0,
          },
        },
        distribuicao_municipios: {
          modo: distribuicaoModo,
          limite_padrao_municipal:
            !semLimiteVagas && modoDistribuicao === "IGUAL" && permiteLimiteMunicipal
              ? Number(limitePorMunicipio) || 0
              : null,
          limite_padrao_estadual:
            !semLimiteVagas && modoDistribuicao === "IGUAL" && permiteLimiteEstadual
              ? Number(limitePorEstado) || 0
              : null,
          municipios:
            !semLimiteVagas && modoDistribuicao === "IGUAL" && permitirExcecoes
              ? selectedMunicipioItems.map((item) => ({
                  id: item.id,
                  nome: item.nome,
                  sigla: item.sigla,
                  estado_id: item.estado_id,
                  codigo_ibge: item.codigo_ibge,
                  limite_municipal: permiteLimiteMunicipal ? Number(item.limite_municipal) || 0 : 0,
                  limite_estadual: permiteLimiteEstadual ? Number(item.limite_estadual) || 0 : 0,
                  origem: item.origem ?? "MANUAL",
                }))
              : [],
        },
      };

      const response = await saveConfigVagasTurma(turmaId, payload);
      if (!response?.success) {
        setMsgError("Nao foi possivel salvar as configuracoes de vagas para a turma.");
        return false;
      }

      setMsgSuccess("Configuracoes de vagas cadastradas com sucesso.");
      return true;
    } catch {
      setMsgError("Erro ao salvar as configuracoes de vagas para a turma.");
      return false;
    } finally {
      setLoadingSave(false);
    }
  };

  const carregarResumoVagasTurma = useCallback(async (currentTurmaId: string) => {
    setLoadingConfigVagas(true);
    try {
      const response = await findResumoVagasTurma(currentTurmaId);
      if (!response) return;

      setSemLimiteVagas(Boolean(response.sem_limite_vagas));
      setCriterio((response.criterio_ocupacao as CriterioOcupacao) || "LIVRE");
      setMetodoSelecao("REGIONAL");
      setSelectedRegional([]);
      setSelectedMunicipiosManual([]);
      setLimiteTotal(Number(response.limites?.total) || 0);
      setLimiteMunicipal(Number(response.limites?.ambitos?.municipal) || 0);
      setLimiteEstadual(Number(response.limites?.ambitos?.estadual) || 0);

      const responseModo = response.distribuicao_municipios?.modo;
      setModoDistribuicao(responseModo === "SEM_LIMITE" ? "SEM_LIMITE" : "IGUAL");
      setPermitirExcecoes(responseModo === "DIFERENTE");
      setLimitePorMunicipio(
        Number(
          response.distribuicao_municipios?.limite_padrao_municipal ??
            response.distribuicao_municipios?.limite_padrao ??
            0,
        ) || 0,
      );
      setLimitePorEstado(Number(response.distribuicao_municipios?.limite_padrao_estadual ?? 0) || 0);

      const municipiosResumo = Array.isArray(response.distribuicao_municipios?.municipios)
        ? response.distribuicao_municipios.municipios
        : Array.isArray(response.municipios)
          ? response.municipios
          : [];
      setSelectedMunicipioItems(
        sortMunicipiosByNome(municipiosResumo.map(mapMunicipioResumo)),
      );
    } finally {
      setLoadingConfigVagas(false);
    }
  }, []);

  const carregarPublicoTurma = useCallback(async (currentTurmaId: string) => {
    setPublicoTurmaCarregado(false);
    try {
      const response = await findPublicoTurma(currentTurmaId);
      const ambitos = Array.isArray(response?.ambitos) ? response.ambitos.map((item) => Number(item)) : [];
      setPermiteLimiteEstadual(ambitos.includes(1));
      setPermiteLimiteMunicipal(ambitos.includes(2));
    } catch {
      setPermiteLimiteEstadual(false);
      setPermiteLimiteMunicipal(false);
    } finally {
      setPublicoTurmaCarregado(true);
    }
  }, []);

  useEffect(() => {
    if (!turmaId) return;
    carregarResumoVagasTurma(turmaId);
    carregarPublicoTurma(turmaId);
  }, [carregarPublicoTurma, carregarResumoVagasTurma, turmaId]);

  return {
    msgError,
    msgSuccess,
    estados,
    regionais,
    municipios,
    loadingConfigVagas,
    loadingRegionais,
    loadingMunicipios,
    loadingSave,
    metodoSelecao,
    setMetodoSelecao,
    selectedRegional,
    setSelectedRegional,
    selectedMunicipiosManual,
    setSelectedMunicipiosManual,
    selectedMunicipios,
    selectedMunicipioItems,
    criterio,
    setCriterio,
    semLimiteVagas,
    setSemLimiteVagas,
    limiteTotal,
    setLimiteTotal,
    limiteMunicipal,
    setLimiteMunicipal: setLimiteMunicipalSafe,
    limiteEstadual,
    setLimiteEstadual: setLimiteEstadualSafe,
    modoDistribuicao,
    setModoDistribuicao,
    limitePorMunicipio,
    setLimitePorMunicipio,
    limitePorEstado,
    setLimitePorEstado,
    permitirExcecoes,
    togglePermitirExcecoes,
    permiteLimiteMunicipal,
    permiteLimiteEstadual,
    publicoTurmaCarregado,
    somaAmbitos,
    excedeLimiteTotal,
    mediaVagasPorMunicipio,
    addMunicipiosManual,
    addMunicipiosByRegionais,
    addTodosMunicipiosEstado,
    addMunicipiosFromCsvImport,
    removeMunicipio,
    clearMunicipios,
    updateMunicipioNumVagas,
    saveConfigVagas,
  };
};
